import React, { useEffect, useState } from "react";
import { Modal, Upload, Input, Select, message, Radio } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import useAxiosInstance from "../../lib/useAxiosInstance";

const { TextArea } = Input;
const { Dragger } = Upload;

const ElectionModeUploadForm = ({ isOpen, onClose, onCreated }) => {
  const axiosInstance = useAxiosInstance();

  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [form20Files, setForm20Files] = useState([]);
  const [erFiles, setErFiles] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [electionModeType, setElectionModeType] = useState("assembly");
  const [electionModeName, setElectionModeName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    fetchClients();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // The client selected in the dropdown, used to determine whether Assembly
  // uploads need a Ward column (Urban) or Village + Block columns (Rural).
  const selectedClient = clients.find((c) => c._id === selectedClientId);
  const isAssembly = electionModeType === "assembly";
  const urbanRural = selectedClient?.urbanRural;

  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      const res = await axiosInstance.get("api/electionMode/clients");
      setClients(res.data?.data || []);
    } catch (error) {
      console.error(error);
      message.error("Failed to load election mode clients");
    } finally {
      setLoadingClients(false);
    }
  };

  const resetForm = () => {
    setForm20Files([]);
    setErFiles([]);
    setSelectedClientId(null);
    setElectionModeType("assembly");
    setElectionModeName("");
    setDescription("");
  };

  const handleCancel = () => {
    resetForm();
    onClose && onClose();
  };

  const handleSubmit = async () => {
    if (!form20Files.length) {
      message.error("Please upload at least one Form20 file");
      return;
    }

    if (!erFiles.length) {
      message.error("Please upload Electoral Roll file");
      return;
    }

    if (!selectedClientId) {
      message.error("Please select a client");
      return;
    }

    if (isAssembly && !urbanRural) {
      message.error(
        "This client has no Urban/Rural type set. Please set it on the client before uploading Assembly election data."
      );
      return;
    }

    if (!electionModeName.trim()) {
      message.error("Election name is required");
      return;
    }

    if (!description.trim()) {
      message.error("Description is required");
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append("client", selectedClientId);
      formData.append("electionModeType", electionModeType);
      formData.append("electionModeName", electionModeName);
      formData.append("description", description);
      form20Files.forEach((file) => {
        formData.append("form20Files", file.originFileObj || file);
      });
      erFiles.forEach((file) => {
        formData.append("erFiles", file.originFileObj || file);
      });

      const res = await axiosInstance.post("api/electionMode", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      message.success(res.data?.message || "Election mode created");
      onCreated && onCreated();
      resetForm();
      onClose && onClose();
    } catch (error) {
      console.error(error);
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        "Something went wrong while creating election mode";
      message.error(backendMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const uploadProps = (fileList, setter) => ({
    multiple: true,
    beforeUpload: () => false,
    fileList,
    accept: ".csv,.xls,.xlsx",
    onChange: ({ fileList: nextFileList }) => setter(nextFileList),
    onRemove: (file) => {
      setter(fileList.filter((item) => item.uid !== file.uid));
    },
  });

  return (
    <Modal
      className="share-report-modal edit-reports"
      open={isOpen}
      onCancel={handleCancel}
      width={860}
      maskClosable={false}
      okText="Submit"
      onOk={handleSubmit}
      confirmLoading={submitting}
    >
      <div className="modal-body-container">
        <div className="modal-title">
          <h3>Upload Election Mode</h3>
        </div>

        <div className="modal-wrapper-content">
          <h4 className="pb-4">Upload Form</h4>

          <div className="add-modal-row">
            <label>Form20 Upload</label>
            <Dragger {...uploadProps(form20Files, setForm20Files)}>
              <p className="ant-upload-drag-icon">
                <FileExcelOutlined />
              </p>
              <p className="ant-upload-text">Click here to upload or drag drop</p>
              <p className="ant-upload-hint">Multiple Excel / CSV files allowed</p>
            </Dragger>
          </div>

          <div className="add-modal-row">
            <label>Electoral Roll Upload</label>
            <Dragger {...uploadProps(erFiles, setErFiles)}>
              <p className="ant-upload-drag-icon">
                <FileExcelOutlined />
              </p>
              <p className="ant-upload-text">Click here to upload or drag drop</p>
              <p className="ant-upload-hint">Multiple Excel / CSV files allowed</p>
            </Dragger>
          </div>

          <h4 className="py-4">Election Details</h4>

          <div className="add-modal-row">
            <label>Client</label>
            <Select
              className="custom-dropdown"
              style={{ width: "100%" }}
              placeholder="Select client"
              showSearch
              value={selectedClientId}
              onChange={setSelectedClientId}
              loading={loadingClients}
              filterOption={(input, option) =>
                option?.children
                  ?.toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {clients.map((client) => (
                <Select.Option key={client._id} value={client._id}>
                  {`${client.adminFirstName || ""} ${
                    client.adminLastName || ""
                  }`.trim() ||
                    client.organisationName ||
                    client.adminEmail}
                  {client.urbanRural ? ` (${client.urbanRural})` : ""}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="add-modal-row">
            <label>Election Mode Type</label>
            <Radio.Group
              value={electionModeType}
              onChange={(e) => setElectionModeType(e.target.value)}
              buttonStyle="solid"
            >
              <Radio.Button value="assembly">Assembly</Radio.Button>
              <Radio.Button value="parliamentary">Parliamentary</Radio.Button>
              <Radio.Button value="state">State</Radio.Button>
            </Radio.Group>
          </div>

          {isAssembly && (
            <div className="add-modal-row">
              {!selectedClientId ? (
                <div style={{ fontSize: 13, color: "#8c8c8c" }}>
                  Select a client above to see the required Assembly sheet columns.
                </div>
              ) : !urbanRural ? (
                <div style={{ fontSize: 13, color: "#cf1322" }}>
                  This client has no Urban/Rural type set. Set it on the
                  client before uploading Assembly election data.
                </div>
              ) : urbanRural === "Rural" ? (
                <div style={{ fontSize: 13, color: "#389e0d" }}>
                  <strong>{selectedClient?.organisationName || "This client"}</strong>{" "}
                  is <strong>Rural</strong> — the Form20 and Electoral Roll
                  sheets must contain <strong>Village</strong> and{" "}
                  <strong>Block</strong> columns instead of Constituency,
                  District and Parliamentary.
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "#389e0d" }}>
                  <strong>{selectedClient?.organisationName || "This client"}</strong>{" "}
                  is <strong>Urban</strong> — the Form20 and Electoral Roll
                  sheets must contain a <strong>Ward</strong> column instead
                  of Constituency, District and Parliamentary.
                </div>
              )}
            </div>
          )}

          <div className="add-modal-row">
            <label>Name of Election</label>
            <Input
              placeholder="Type election name"
              value={electionModeName}
              onChange={(e) => setElectionModeName(e.target.value)}
            />
          </div>

          <div className="add-modal-row">
            <label>Description</label>
            <TextArea
              rows={3}
              placeholder="Write description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ElectionModeUploadForm;
